import { useNavigate } from 'react-router-dom';
import { DollarSign, Trophy, Users, AlertTriangle, Target, UserCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { CapacityBar } from '@/components/events/CapacityBar';
import { EventCardPrize } from '@/components/events/EventCardPrize';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useEventCapacity } from '@/api/extensions/useRegistrationOps';
import { useStripeFeeConfig } from '@/api/extensions/useStripeFeeConfig';
import { calculateTransactionFee } from '@/utils/stripe-fee-calculator';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters';
import { getEventDisplayTitle, isTeamRopingEvent } from '@/utils/eventTypes';

interface EventCardProps {
  event: {
    id: string;
    event_name?: string | null;
    event_type?: string | null;
    trig_event_type_name?: string | null;
    entry_fee?: number;
    transaction_fee?: number;
    total_prize_amount?: number;
    header_max_handicap?: number;
    heeler_max_handicap?: number;
    max_combined_handicap?: number;
    max_individual_handicap?: number;
    max_contestants?: number;
    total_rounds?: number;
    event_status?: string;
    trig_status_name?: string;
    [key: string]: unknown;
  };
  registrationStatus?: {
    id: string;
    status?: string;
    trig_status_name?: string;
    position?: string;
    partner_status?: string;
    waitlist_position?: number;
    [key: string]: unknown;
  } | null;
  userHandicaps?: Array<{
    rating: number;
    handicap_type_id: string;
    [key: string]: unknown;
  }>;
  capacity?: {
    total_capacity: number;
    registered_count: number;
    available_spots: number;
    waitlist_count: number;
  } | null;
  /**
   * Buddy-group registration indicators for this event. When present, the
   * card surfaces avatar bubbles for every group member (including self)
   * who is actively registered, so the user can see at-a-glance which
   * events the group is filling out. Self gets a primary fill, teammates
   * get an outlined accent.
   */
  buddyGroupRegistrations?: Array<{
    user_profile_id: string;
    display_name: string | null;
    is_self: boolean;
  }>;
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase();
}

export function EventCard({
  event,
  registrationStatus,
  userHandicaps,
  capacity,
  buddyGroupRegistrations,
}: EventCardProps) {
  const navigate = useNavigate();
  const entryFee = Number(event.entry_fee) || 0;
  const storedTxnFee = Number(event.transaction_fee) || 0;
  const prizeAmount = Number(event.total_prize_amount) || 0;

  // Mirror the backend payment service: honor an explicit per-event
  // override (`events.transaction_fee` > 0) but otherwise derive the fee
  // from the platform Stripe fee config so cards reflect what the payer
  // will actually be charged. Auto-calc events store 0/null and would
  // otherwise render "Free" — see PaymentService.calculateRegistrationFees.
  const { data: stripeFeeConfig } = useStripeFeeConfig();
  const autoTxnFee = stripeFeeConfig
    ? calculateTransactionFee(entryFee, stripeFeeConfig)
    : 0;
  const txnFee = storedTxnFee > 0 ? storedTxnFee : autoTxnFee;

  // Fetch capacity data if not passed as prop
  const { data: fetchedCapacity } = useEventCapacity(event.id);
  const capacityData = capacity ?? fetchedCapacity ?? null;

  // Check handicap eligibility
  const handicapWarning = (() => {
    if (!userHandicaps || userHandicaps.length === 0) return null;
    const maxRating = Math.max(...userHandicaps.map((h) => h.rating));
    if (event.max_individual_handicap && maxRating > (event.max_individual_handicap as number)) {
      return `Your HC (${maxRating}) exceeds max (${event.max_individual_handicap})`;
    }
    return null;
  })();

  // Capacity (kept for future use — currently unused)
  void capacity;

  // Registration status — the row comes from /registration-ops/my-registrations
  // which exposes `trig_status_name` (canonical) and may also carry a `status`
  // field on derived/normalized shapes. Read both so the cancel guard and the
  // Pay Now button work regardless of which variant the parent passes in.
  const regStatus =
    (registrationStatus?.trig_status_name as string | undefined) ??
    (registrationStatus?.status as string | undefined);

  // Self-registered indicator: any active row from `myRegistrations` (the
  // parent passes `registrationStatus` filtered to this event already).
  // Cancelled rows shouldn't reach this card, but guard anyway.
  const isSelfRegistered =
    !!registrationStatus && regStatus !== 'cancelled';

  // Group membership indicators — sort self first, then teammates by name.
  const sortedBuddyMembers = (buddyGroupRegistrations ?? [])
    .slice()
    .sort((a, b) => {
      if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
      return (a.display_name ?? '').localeCompare(b.display_name ?? '');
    });
  const visibleBuddyMembers = sortedBuddyMembers.slice(0, 3);
  const overflowBuddyCount = Math.max(
    0,
    sortedBuddyMembers.length - visibleBuddyMembers.length,
  );

  // Tint the whole card when any group member (self or teammate) is on
  // the event roster — makes "this event has me/us in it" pop at a glance.
  const hasGroupRegistration = sortedBuddyMembers.length > 0 || isSelfRegistered;

  return (
    <Card
      className={cn(
        'flex flex-col h-full hover:shadow-md hover:border-primary/30 transition-all duration-200 cursor-pointer overflow-hidden',
        hasGroupRegistration && 'bg-primary/5',
        isSelfRegistered &&
          'border-l-4 border-l-primary ring-1 ring-primary/20',
      )}
      onClick={() => navigate(`/events/${event.id}`)}
      aria-label={
        isSelfRegistered
          ? `${getEventDisplayTitle(event)} — you are entered`
          : undefined
      }
    >
      <CardContent className="p-0 flex flex-col flex-1">
        {/* Card header with event icon + name */}
        <div className="flex items-center gap-3 p-4 pb-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Trophy className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-sm leading-tight truncate">
              {getEventDisplayTitle(event)}
            </h3>
            {event.trig_status_name && (
              <StatusBadge status={event.trig_status_name} variant="event" className="mt-1" />
            )}
          </div>
          {/* Registration status badge */}
          {regStatus && (
            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
              {regStatus === 'pending_payment' ? (
                <Button
                  size="sm"
                  variant="destructive"
                  className="animate-pulse text-xs h-7"
                  onClick={() => navigate(`/events/${event.id}?action=pay`)}
                >
                  Pay Now
                </Button>
              ) : (
                <StatusBadge status={regStatus} variant="registration" />
              )}
            </div>
          )}
        </div>

        {/* Fee + Prize row */}
        <div className="flex items-center gap-4 px-4 pb-3 text-sm">
          {entryFee > 0 && (
            <span className="flex items-center gap-1 text-emerald-600 font-medium">
              <DollarSign className="h-3.5 w-3.5" />
              Entry: {formatCurrency(entryFee)}
            </span>
          )}
          <EventCardPrize
            eventId={event.id}
            isDynamic={(event.prize_allocation_mode as string) === 'dynamic'}
            displayEnabled={!!event.display_prize_to_contestants}
            staticPrize={prizeAmount}
          />
        </div>

        {/* Transaction fee */}
        {txnFee > 0 ? (
          <div className="px-4 pb-2 text-xs text-muted-foreground">
            Transaction Fee: {formatCurrency(txnFee)}
          </div>
        ) : (
          <div className="px-4 pb-2 text-xs text-muted-foreground">
            Transaction Fee: Free
          </div>
        )}

        {/* Details row: handicap */}
        {event.max_combined_handicap != null && (
          <div className="flex flex-wrap gap-2 px-4 pb-3">
            <Badge variant="outline" className="text-xs font-normal">
              <Target className="h-3 w-3 mr-1" />
              Max HC: {event.max_combined_handicap}
            </Badge>
          </div>
        )}

        {/* Handicap warning */}
        {handicapWarning && (
          <div className="flex items-center gap-1.5 px-4 pb-2 text-xs text-red-600">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span>{handicapWarning}</span>
          </div>
        )}

        {/* Buddy-group registration indicators — only renders when the
            current user is in a buddy group attached to this rodeo and
            at least one group member (self or teammate) is registered. */}
        {sortedBuddyMembers.length > 0 && (
          <div
            className="flex items-center gap-2 px-4 pb-3"
            onClick={(e) => e.stopPropagation()}
          >
            <UserCheck
              className="h-3.5 w-3.5 text-muted-foreground shrink-0"
              aria-hidden="true"
            />
            <span className="sr-only">Buddy group members entered:</span>
            <TooltipProvider delayDuration={150}>
              <div className="flex -space-x-1.5">
                {visibleBuddyMembers.map((m) => {
                  const label = m.is_self
                    ? `You (${m.display_name ?? 'You'}) are entered`
                    : `${m.display_name ?? 'Buddy member'} is entered`;
                  return (
                    <Tooltip key={m.user_profile_id}>
                      <TooltipTrigger asChild>
                        <span
                          aria-label={label}
                          className={cn(
                            'inline-flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-semibold ring-2 ring-background',
                            m.is_self
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-foreground border-primary/60',
                          )}
                        >
                          {getInitials(m.display_name)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{label}</TooltipContent>
                    </Tooltip>
                  );
                })}
                {overflowBuddyCount > 0 && (
                  <span
                    aria-label={`${overflowBuddyCount} more buddy member${overflowBuddyCount === 1 ? '' : 's'} entered`}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-muted-foreground/30 bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-background"
                  >
                    +{overflowBuddyCount}
                  </span>
                )}
              </div>
            </TooltipProvider>
            <span className="text-xs text-muted-foreground">
              {sortedBuddyMembers.length === 1 && sortedBuddyMembers[0]!.is_self
                ? 'You are entered'
                : `${sortedBuddyMembers.length} of your group entered`}
            </span>
          </div>
        )}

        {/* Registration capacity */}
        {(() => {
          const registered = capacityData?.registered_count ?? 0;
          const total = capacityData?.total_capacity ?? (event.max_contestants ? Number(event.max_contestants) : 0);
          const isOver = total > 0 && registered > total;
          const pct = total > 0 ? Math.round((registered / total) * 100) : 0;

          // Team roping stores 1 row per person (Header + Heeler), and
          // `max_contestants` is the people-cap. Surface the count as teams
          // for team events so "10 entries / 20 cap" reads as "5 / 10 teams".
          // Math.ceil on registered so an orphan row (partner not yet
          // accepted) counts as a team in progress; Math.floor on total
          // since you can't have half a team of capacity. Ratios/percentages
          // stay on the raw people counts — same value either way.
          const isTeamEvent = isTeamRopingEvent(event);
          const displayRegistered = isTeamEvent ? Math.ceil(registered / 2) : registered;
          const displayTotal = isTeamEvent ? Math.floor(total / 2) : total;
          const unit = isTeamEvent ? 'team' : 'entry';
          const unitPlural = isTeamEvent ? 'teams' : 'entries';
          const overDisplay = isOver ? Math.max(0, displayRegistered - displayTotal) : 0;
          const overPct = isOver && total > 0 ? Math.round(((registered - total) / total) * 100) : 0;

          let subtext: string;
          if (total <= 0) subtext = 'Open registration';
          else if (isOver) subtext = `Full · ${overDisplay} over (+${overPct}%)`;
          else subtext = `${pct}% full`;

          const countText = total > 0
            ? `${displayRegistered} of ${displayTotal} ${unitPlural}`
            : `${displayRegistered} ${displayRegistered === 1 ? unit : unitPlural} entered`;

          return (
            <div className="px-4 pb-3 space-y-1.5">
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {isTeamEvent ? 'Teams' : 'Entries'}
                </span>
                <span className={cn('font-medium', isOver ? 'text-destructive' : 'text-foreground')}>
                  {countText}
                </span>
              </div>
              {total > 0 && <CapacityBar registered={registered} total={total} />}
              <p className={cn('text-[10px] text-center', isOver ? 'text-destructive' : 'text-muted-foreground')}>
                {subtext}
              </p>
            </div>
          );
        })()}

        {/* Divider + action button — `mt-auto` pushes the button to the
            bottom of the card so all cards in a row align their CTAs even
            when the section above varies in height (e.g. some cards have
            a buddy-group indicator row, others don't). */}
        <div className="border-t px-4 py-3 mt-auto">
          <Button
            className="w-full"
            size="sm"
            variant={regStatus === 'pending_payment' ? 'destructive' : 'default'}
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                regStatus === 'pending_payment'
                  ? `/events/${event.id}?action=pay`
                  : `/events/${event.id}`,
              );
            }}
          >
            {!registrationStatus
              ? 'View Event Details'
              : regStatus === 'pending_payment'
                ? 'Pay Now'
                : 'View Event Details'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
